import type { CodeableConcept } from "./common";
import { PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";

/** Stable Practitioner domain identity, independent of the FHIR locator. */
export type PractitionerId = DomainId<"PractitionerId">;

export function asPractitionerId(value: string): PractitionerId {
  if (!value) throw new PreconditionFailedError("A Practitioner identity cannot be empty");
  return value as PractitionerId;
}

// ============================================================
// Practitioner — a clinician (ADR-0017)
//
// Anchor + PractitionerRole (0..1 contextual): the role is not a separate thing
// a caller manages, it is how this clinician's specialty and code are stored,
// so it lives inside the aggregate. Like a device or a room, a clinician is a
// schedulable resource, and — see [[device.model]] — knows nothing about those
// calendars: a Schedule is its own aggregate.
//
// THERE IS NO `deregister` HERE, and no registration handler either. Taking a
// clinician off the roster has to close their calendar in the same session, and
// opening one needs their real working hours: the unconstrained window a room
// gets reads, on a person, as a clinician with no hours and no weekends. Both
// wait for the change that knows what those hours are.
//
// The Google Workspace address is the identity domain-wide delegation
// impersonates to mint Meet spaces. Only an address in our own Workspace domain
// can ever be impersonated, so anything else is refused at the point of entry
// rather than failing later inside a conferencing call.
// ============================================================

const GOOGLE_WORKSPACE_DOMAIN = "mindforge.health";

interface Role {
  code?: CodeableConcept;
  specialty?: CodeableConcept[];
  active?: boolean;
}

interface PractitionerFields {
  active?: boolean;
  name?: string;
  timezone?: string;
  googleWorkspaceEmail?: string;
  role: Role | null;
}

export interface CreatePractitionerInput extends PractitionerFields {
  id: string;
}

export interface PractitionerProps extends PractitionerFields {
  id: PractitionerId;
}

export class Practitioner {
  private state: PractitionerProps;

  private constructor(props: PractitionerProps) {
    this.state = structuredClone(props);
  }

  // ── facts ──

  static create(input: CreatePractitionerInput): Practitioner {
    return new Practitioner({ ...structuredClone(input), id: asPractitionerId(input.id) });
  }

  static restore(snapshot: PractitionerProps): Practitioner {
    return new Practitioner(snapshot);
  }

  get id(): PractitionerId {
    return this.state.id;
  }

  get isActive(): boolean {
    return this.state.active !== false;
  }

  /** The identity to impersonate for conferencing, when they have one. */
  get googleWorkspaceEmail(): string | undefined {
    return this.state.googleWorkspaceEmail;
  }

  snapshot(): PractitionerProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  setTimezone(timezone: string): void {
    if (!timezone) {
      throw new PreconditionFailedError("Timezone is required");
    }
    this.state.timezone = timezone;
  }

  /**
   * Set the Workspace login. Normalised to lower case, because impersonation
   * matches on the exact string and a capitalised address is the same person.
   */
  setGoogleWorkspaceEmail(email: string): void {
    const at = email.indexOf("@");
    if (!email || at <= 0 || at === email.length - 1) {
      throw new PreconditionFailedError("Google Workspace email is required and must be a valid address");
    }
    const normalized = email.toLowerCase();
    if (normalized.slice(normalized.indexOf("@") + 1) !== GOOGLE_WORKSPACE_DOMAIN) {
      throw new PreconditionFailedError(`Google Workspace email must be in the ${GOOGLE_WORKSPACE_DOMAIN} domain`);
    }
    this.state.googleWorkspaceEmail = normalized;
  }

  /** Record what this clinician practises. Creates the role row if they had none. */
  setRole(role: Role): void {
    this.state.role = { ...this.state.role, ...role };
  }
}
